import i18next from 'i18next';
import Locize from 'i18next-locize-backend';
import { initReactI18next } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { createLogger } from '@utils/logger';

const log = createLogger('i18n');

const isProd = import.meta.env.PROD;

const SUPPORTED_LNGS = ['en', 'cs', 'de', 'hu', 'pl', 'fr'];
const FALLBACK_LNG = 'cs';
const LOCIZE_PROJECT_ID = '989c7799-4eae-4b1b-90db-cdae2f7441de';
const NS = 'common';
const LOCIZE_TIMEOUT_MS = 5000;
const SLOW_LOAD_MS = 3000;
const MAX_CONSECUTIVE_FAILURES = 3;
const CACHE_KEY_PREFIX = 'i18n:v1:';
const I18NEXT_LANGUAGE_KEY = 'i18nextLng';
// A real namespace has thousands of keys; guards against accidentally caching an empty/partial
// Locize response.
const MIN_CACHEABLE_KEYS = 100;

type NamespaceDict = Record<string, unknown>;

const cacheKey = (lng: string) => `${CACHE_KEY_PREFIX}${lng}`;

/** `cs-CZ` → `cs`. Exported so callers that ship a language code to a backend send the same form. */
export const normalizeLng = (value?: string | null): string | undefined => value?.split('-')[0]?.toLowerCase();

// The single source of truth for the active language: its result keys the localStorage cache, is
// seeded into i18next and is passed to init() as `lng`. Resolution order matches what
// LanguageDetector used to do (path → localStorage → navigator); the URL prefix has to win, because
// the language switcher works by rewriting it and hard-reloading. Every candidate is validated
// against SUPPORTED_LNGS, so `/public/...` and other prefix-less URLs fall through to the stored
// preference instead of being read as a language. Always returns a supported language, which also
// keeps the declared `: string` contract — a null here used to reach
// i18next.getResourceBundle(null) and crash on lng.indexOf. (Sentry SF.)
const detectLanguage = (): string => {
  const fromPath = normalizeLng(window.location.pathname.split('/')[1]);
  if (fromPath && SUPPORTED_LNGS.includes(fromPath)) return fromPath;

  const stored = normalizeLng(localStorage.getItem(I18NEXT_LANGUAGE_KEY));
  if (stored && SUPPORTED_LNGS.includes(stored)) return stored;

  const fromNavigator = normalizeLng(navigator.language);
  if (fromNavigator && SUPPORTED_LNGS.includes(fromNavigator)) return fromNavigator;

  return FALLBACK_LNG;
};

const readCachedSnapshot = (lng: string): NamespaceDict | undefined => {
  try {
    const raw = localStorage.getItem(cacheKey(lng));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as NamespaceDict) : undefined;
  } catch {
    return undefined;
  }
};

const writeCachedSnapshot = (lng: string, dict: NamespaceDict | string): void => {
  try {
    const value = typeof dict === 'string' ? dict : JSON.stringify(dict);
    localStorage.setItem(cacheKey(lng), value);
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'i18n',
      level: 'warning',
      message: `i18n cache write failed: ${(err as Error).message ?? 'unknown'}`,
    });
  }
};

// Observability for the Locize backend (cache-miss prod path + dev). Background refresh has its
// own breadcrumbs; this block stays scoped to the i18next-driven load.
let consecutiveFailures = 0;

i18next.on('loaded', () => {
  consecutiveFailures = 0;
});

i18next.on('failedLoading', (lng, ns, msg) => {
  consecutiveFailures += 1;
  console.warn(`[i18n] Locize load failed for ${lng}/${ns}: ${msg}`);
  Sentry.addBreadcrumb({ category: 'i18n', level: 'warning', message: `Locize load failed for ${lng}/${ns}: ${msg}` });
  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    Sentry.captureMessage(`Locize sync failing (${consecutiveFailures}x) — running on cached translations`, 'warning');
  }
});

const reportSlowLoad = (url: string, elapsedMs: number) => {
  const rounded = Math.round(elapsedMs);
  console.warn(`[i18n] slow Locize load (${rounded}ms): ${url}`);
  Sentry.addBreadcrumb({ category: 'i18n', level: 'warning', message: `slow Locize load (${rounded}ms): ${url}` });
};

// Custom backend request: aborts after LOCIZE_TIMEOUT_MS and times GET loads to flag
// degradation. Only used by i18next-locize-backend (cache-miss prod / dev).
const locizeRequest = (
  info: { url: string; method: 'GET' | 'POST'; body?: unknown; headers?: Record<string, string> },
  callback: (err: unknown, res?: { status: number; data?: string }) => void,
) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOCIZE_TIMEOUT_MS);
  const startedAt = performance.now();
  fetch(info.url, {
    method: info.method,
    headers: info.headers,
    body: info.body ? JSON.stringify(info.body) : undefined,
    signal: controller.signal,
  })
    .then((response) => {
      const elapsed = performance.now() - startedAt;
      if (info.method === 'GET' && elapsed > SLOW_LOAD_MS) reportSlowLoad(info.url, elapsed);
      if (!response.ok) {
        callback(response.statusText || 'Error', { status: response.status });
        return;
      }
      response
        .text()
        .then((data) => callback(null, { status: response.status, data }))
        .catch(callback);
    })
    .catch(callback)
    .finally(() => clearTimeout(timer));
};

// Background refresh path: plain fetch that writes localStorage only. Never merges into
// i18next so the running tab is never re-rendered mid-session because of a translator update.
// The next tab/reload reads the fresher value.
const refreshCacheFor = async (lng: string): Promise<void> => {
  log.debug(`refreshing cache for ${lng} in background`);
  const url = `https://api.locize.app/${LOCIZE_PROJECT_ID}/latest/${lng}/${NS}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOCIZE_TIMEOUT_MS);
  const startedAt = performance.now();
  try {
    const res = await fetch(url, { signal: controller.signal });
    const elapsed = performance.now() - startedAt;
    if (elapsed > SLOW_LOAD_MS) reportSlowLoad(url, elapsed);
    if (!res.ok) {
      Sentry.addBreadcrumb({
        category: 'i18n',
        level: 'warning',
        message: `i18n background refresh non-2xx for ${lng}: ${res.status}`,
      });
      return;
    }
    const body = await res.text();
    // Soft-validate before persisting so a malformed CDN response can't corrupt the cache.
    let parsed: unknown;
    try {
      parsed = JSON.parse(body);
    } catch {
      Sentry.addBreadcrumb({
        category: 'i18n',
        level: 'warning',
        message: `i18n background refresh parse failure for ${lng}`,
      });
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || Object.keys(parsed as object).length < MIN_CACHEABLE_KEYS) {
      Sentry.addBreadcrumb({
        category: 'i18n',
        level: 'warning',
        message: `i18n background refresh suspect payload for ${lng}`,
      });
      return;
    }
    writeCachedSnapshot(lng, body);
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'i18n',
      level: 'warning',
      message: `i18n background refresh failed for ${lng}: ${(err as Error).message ?? 'unknown'}`,
    });
  } finally {
    clearTimeout(timer);
  }
};

// Cache-miss prod path + dev path both run the Locize backend. On the first successful load,
// snapshot the merged bundle into localStorage for next session, then detach — we deliberately
// don't keep writing on every later load so dev's saveMissing-driven reloads stay quiet.
const seedCacheOnFirstLoaded = (lng: string) => {
  log.debug(`seeding cache for ${lng} on first loaded event`);
  const onLoaded = () => {
    const bundle = i18next.getResourceBundle(lng, NS) as NamespaceDict | undefined;
    if (bundle && Object.keys(bundle).length >= MIN_CACHEABLE_KEYS) {
      writeCachedSnapshot(lng, bundle);
      i18next.off('loaded', onLoaded);
    }
  };
  i18next.on('loaded', onLoaded);
};

// Options shared by both init() paths below. `escapeValue: false` is the standard React setting: React escapes
// rendered strings itself, and i18next's own escaping turned interpolated values like a dayjs `L` date under an
// English browser locale into `03&#x2F;01&#x2F;2027` on screen (RD-2963).
const baseInitOptions = (lng: string) => ({
  lng,
  supportedLngs: SUPPORTED_LNGS,
  react: { useSuspense: false },
  ns: [NS],
  debug: false,
  fallbackLng: FALLBACK_LNG,
  interpolation: { escapeValue: false },
});

export const i18nReady = (async () => {
  log.debug('Locize loaded');
  const lng = detectLanguage();
  // Persist the choice ourselves now that LanguageDetector (whose `caches` used to do this) is gone.
  // Without it a language picked via the URL prefix would be forgotten on the next prefix-less URL,
  // e.g. the Auth0 redirect back to the origin.
  localStorage.setItem(I18NEXT_LANGUAGE_KEY, lng);
  const cached = readCachedSnapshot(lng);

  Sentry.addBreadcrumb({
    category: 'i18n',
    level: 'info',
    message: cached
      ? `i18n boot: lng=${lng}, cache=hit (${Object.keys(cached).length} top-level keys)`
      : `i18n boot: lng=${lng}, cache=miss`,
  });

  if (cached && isProd) {
    // Returning-user prod path: no backend plugin. The tab's resource store is the cache and
    // stays frozen for the tab's lifetime. Refresh below updates localStorage for next time.
    await i18next
      .use(initReactI18next)
      .init({
        ...baseInitOptions(lng),
        resources: { [lng]: { [NS]: cached } },
      });
  } else {
    // Cache-miss prod OR any dev session: keep the Locize backend so the first response merges
    // into the running store and so dev's saveMissing keeps pushing new keys to Locize.
    // Only seed from the backend-merged bundle when nothing is cached yet. On a cache hit (dev)
    // the first `loaded` can fire before the merge completes, so seeding here would persist a
    // stale pre-merge bundle; the post-init refresh keeps an existing cache fresh instead.
    if (!cached) seedCacheOnFirstLoaded(lng);
    await i18next
      .use(Locize)
      .use(initReactI18next)
      .init({
        ...baseInitOptions(lng),
        resources: cached ? { [lng]: { [NS]: cached } } : undefined,
        partialBundledLanguages: Boolean(cached),
        backend: isProd
          ? {
              request: locizeRequest,
              projectId: LOCIZE_PROJECT_ID,
              version: 'latest',
              private: false,
            }
          : {
              request: locizeRequest,
              projectId: LOCIZE_PROJECT_ID,
              apiKey: '6fad0897-e6b0-4445-8288-80dabb5b2405',
              referenceLng: 'cs',
              private: false,
              version: 'latest',
              allowedAddOrUpdateHosts: ['localhost', 'logsteo-alpha.os.shipvio.com', 'alpha.ringil.com'],
              onSaved: () => {},
              // autotranslate: machine-translate each newly added key into the other target languages.
              // autotranslatereview: route those machine translations through Locize's review workflow
              // (unpublished/pending) instead of publishing them directly.
              addPath: 'https://api.locize.app/missing/{{projectId}}/{{version}}/{{lng}}/{{ns}}?autotranslate=true&autotranslatereview=true',
              reloadInterval: 60000000,
            },
        saveMissing: !isProd,
      });
  }

  // Whenever we booted from a cache (prod OR dev), refresh it ASAP from Locize for the next
  // reload. Plain fetch that writes localStorage only — it never merges into the running store,
  // so a translator update can't re-render the live tab and startup never hard-depends on Locize.
  // Cache-miss paths already pulled fresh data via the backend + `seedCacheOnFirstLoaded` above.
  if (cached) {
    void refreshCacheFor(lng);
  }
})();
